import React, { useState, useEffect } from 'react';
import CompanyUserManagement from '../components/CompanyUserManagement';
import { api } from '../utils/api';

const UserManagement = ({ userInfo }) => {
  const [selectedCompany, setSelectedCompany] = useState('');
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  // Debug userInfo
  console.log('UserManagement - userInfo:', userInfo);

  // For super clients, use their company directly
  // For admins/super clients with selectedCompany, use that
  useEffect(() => {
    console.log('UserManagement useEffect - userInfo:', userInfo);
    if (userInfo?.isSuperUser && userInfo?.companyName) {
      // Super client with their own company
      setSelectedCompany(userInfo.companyName);
    } else if (userInfo?.selectedCompany) {
      // Admin or super client that has selected a company
      setSelectedCompany(userInfo.selectedCompany);
    } else if (userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin') {
      // For admins without a selected company, fetch available companies
      fetchAvailableCompanies();
    }
  }, [userInfo]);

  const fetchAvailableCompanies = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/auth/companies`);
      if (response.ok) {
        const companies = await response.json();
        setAvailableCompanies(companies);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
    setLoading(false);
  };

  // Show loading state while userInfo is being loaded
  if (!userInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="card p-6 max-w-md mx-auto">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading user information...</span>
          </div>
        </div>
      </div>
    );
  }

  // Only Founders, Super Users and StreamLaunch admins manage users.
  const canManageUsers = userInfo?.isSuperUser ||
                        userInfo?.companyRole === 'founder' ||
                        userInfo?.companyRole === 'super_user' ||
                        userInfo?.accountType === 'admin' ||
                        userInfo?.accountType === 'superadmin';

  if (!canManageUsers) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="card p-6 max-w-md mx-auto">
          <p className="text-center text-gray-600">User management is only available to Founders, Super Users and administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="card p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">User Management</h2>
              <p className="text-gray-600">
                {userInfo?.isSuperUser 
                  ? `Manage the list of users who are authorized to register for ${userInfo?.companyName || userInfo?.selectedCompany || 'your company'}`
                  : userInfo?.selectedCompany
                  ? `Managing users for ${userInfo.selectedCompany}`
                  : userInfo?.accountType === 'superadmin'
                  ? 'Manage users across all companies'
                  : 'Manage users for your assigned companies'
                }
              </p>
            </div>
          </div>
        </div>
        
        {/* Company Selection for Admins - only show if no company selected yet */}
        {(userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin') && !userInfo?.isSuperUser && !userInfo?.selectedCompany && (
          <div className="card p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Select Company to Manage</h3>
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading companies...</span>
              </div>
            ) : (
              <div className="max-w-md">
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="input-field"
                >
                  <option value="">Select a company</option>
                  {availableCompanies.map((company) => (
                    <option key={company.name} value={company.name}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
        
        {/* Show CompanyUserManagement only when a company is selected */}
        {selectedCompany && (
          <CompanyUserManagement 
            userInfo={userInfo} 
            companyName={selectedCompany} 
          />
        )}
        
        {/* Show message when no company is selected for admins */}
        {(userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin') && !userInfo?.isSuperUser && !userInfo?.selectedCompany && !selectedCompany && !loading && (
          <div className="card p-6">
            <p className="text-center text-gray-600">Please select a company above to manage its users.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;